/**
 * Helpers for rendering player names on coach-facing surfaces in a way that
 * accounts for parent-type accounts (COPPA youth profile model).
 *
 * On a parent-type account, the Auth user is the parent and the "player" is
 * the youth profile attached to the parent's user doc. Coach surfaces should
 * show the playing person (the child) as the primary label, with the parent
 * as a secondary tag so the coach knows they're communicating with an adult
 * intermediary.
 */

import type { Order } from '../types/order';
import type { UserProfile } from '../types/user';

/**
 * Returns the primary player display label.
 *  - Parent account → child's first name (e.g. "Maya")
 *  - Player account → existing displayName / playerName
 */
export function playerDisplayName(input: {
  accountType?: string;
  playerFirstName?: string;
  displayName?: string;
  fallback?: string;
}): string {
  if (input.accountType === 'parent' && input.playerFirstName) {
    return input.playerFirstName;
  }
  return input.displayName || input.fallback || 'Player';
}

/**
 * Secondary parent attribution for parent-type accounts. Returns null for
 * regular player accounts so callers can skip rendering it.
 */
export function parentAttribution(input: {
  accountType?: string;
  parentFirstName?: string;
}): string | null {
  if (input.accountType !== 'parent') return null;
  if (!input.parentFirstName) return null;
  return `Parent: ${input.parentFirstName}`;
}

/**
 * Convenience for surfaces that have an Order (with denormalized
 * playerAccountType / playerParentFirstName) but no full UserProfile read.
 */
export function playerLabelsFromOrder(order: Order): { primary: string; parent: string | null } {
  return {
    primary:
      order.playerAccountType === 'parent' && (order as Order & { playerName?: string }).playerName
        ? order.playerName
        : order.playerName || 'Player',
    parent:
      order.playerAccountType === 'parent' && order.playerParentFirstName
        ? `Parent: ${order.playerParentFirstName}`
        : null,
  };
}

/**
 * Same as playerLabelsFromOrder but takes a full UserProfile (used by surfaces
 * that have already loaded the user doc, e.g. CoachBundleProgress).
 */
export function playerLabelsFromProfile(profile: UserProfile | null | undefined): {
  primary: string;
  parent: string | null;
} {
  if (!profile) return { primary: 'Player', parent: null };
  const isParent = profile.accountType === 'parent';
  return {
    primary: playerDisplayName({
      accountType: profile.accountType,
      playerFirstName: profile.playerFirstName,
      displayName: profile.playerProfile?.displayName || profile.displayName,
    }),
    parent: isParent
      ? parentAttribution({ accountType: 'parent', parentFirstName: profile.parentFirstName })
      : null,
  };
}
